import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { activity } from '@dha-nexus/shared';
import { PageHeader, ErrorState, LoadingSkeleton, EmptyState } from '../../components/index.js';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import './admin.css';

const EMPTY_FILTERS = Object.freeze({
  personId: '',
  sessionId: '',
  environment: '',
  routePath: '',
  eventType: '',
  eventName: '',
  from: '',
  to: '',
  title: '',
});

const LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;

function zonedParts(formatter, instant) {
  return Object.fromEntries(
    formatter
      .formatToParts(new Date(instant))
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)]),
  );
}

export function localDateTimeToInstant(value, timeZone) {
  const match = LOCAL_DATE_TIME.exec(String(value || ''));
  if (!match) throw new Error('Choose a valid local date and time.');
  const desired = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] || 0),
    millisecond: Number(String(match[7] || '').padEnd(3, '0') || 0),
  };
  const naiveInstant = Date.UTC(
    desired.year,
    desired.month - 1,
    desired.day,
    desired.hour,
    desired.minute,
    desired.second,
    desired.millisecond,
  );
  const naiveDate = new Date(naiveInstant);
  if (
    naiveDate.getUTCFullYear() !== desired.year ||
    naiveDate.getUTCMonth() + 1 !== desired.month ||
    naiveDate.getUTCDate() !== desired.day ||
    naiveDate.getUTCHours() !== desired.hour ||
    naiveDate.getUTCMinutes() !== desired.minute ||
    naiveDate.getUTCSeconds() !== desired.second
  ) {
    throw new Error('Choose a valid local date and time.');
  }

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const offsets = new Set();
  for (let hours = -48; hours <= 48; hours += 6) {
    const sample = naiveInstant + hours * 60 * 60 * 1000;
    const parts = zonedParts(formatter, sample);
    const displayedAsUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    offsets.add(displayedAsUtc - Math.floor(sample / 1000) * 1000);
  }
  const candidates = [...offsets]
    .map((offset) => naiveInstant - offset)
    .filter((candidate) => {
      const parts = zonedParts(formatter, candidate);
      return ['year', 'month', 'day', 'hour', 'minute', 'second'].every(
        (key) => parts[key] === desired[key],
      );
    })
    .filter((candidate, index, values) => values.indexOf(candidate) === index);
  if (!candidates.length) {
    throw new Error(`The selected local time does not exist in ${timeZone}. Choose another time.`);
  }
  if (candidates.length > 1) {
    throw new Error(`The selected local time occurs twice in ${timeZone}. Choose another time.`);
  }
  return new Date(candidates[0]).toISOString();
}

export function serializeActivityReportFilters(
  filters,
  { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone } = {},
) {
  const from = filters.from ? localDateTimeToInstant(filters.from, timeZone) : '';
  const to = filters.to ? localDateTimeToInstant(filters.to, timeZone) : '';
  return {
    ...filters,
    from,
    to,
    ...((from || to) && timeZone ? { timeZone } : {}),
  };
}

export function activityReportScopeErrorMessages(error) {
  if (error?.code !== 'ACTIVITY_REPORT_SCOPE_UNREADY') return [];
  const affected = Array.isArray(error.details?.affected) ? error.details.affected : [];
  const messages = affected.map((entry) => {
    const reasons = Array.isArray(entry.reasons) ? entry.reasons : [];
    const reasonText = reasons
      .map(
        (reason) =>
          ({
            heartbeat_stale: 'heartbeat is stale',
            eligible_delivery_pending: `${Number(entry.pendingCount || 0)} eligible deliveries are pending`,
            identity_unresolved: 'assigned tester identity is unresolved',
            identity_resolution_error: `${Number(entry.unresolvedCount || 0)} identity resolutions failed`,
          })[reason] || 'coverage is not ready',
      )
      .join('; ');
    return `${entry.tester || 'Assigned tester'} · ${entry.sandbox || 'Assigned sandbox'}: ${reasonText}.`;
  });
  if (error.details?.regenerationGuidance) messages.push(error.details.regenerationGuidance);
  return messages;
}

const EVENT_TYPE_OPTIONS = activity.ACTIVITY_EVENT_TYPES.map((value) => ({
  value,
  label: activity.activityEventTypeLabel(value),
}));

function eventNameOptionsFor(eventType) {
  const names = eventType
    ? activity.ACTIVITY_EVENT_NAMES_BY_TYPE[eventType] || []
    : activity.ACTIVITY_EVENT_NAMES;
  return names.map((value) => ({
    value,
    label: `${activity.activityEventNameLabel(value)} · ${value}`,
  }));
}

function formatSessionStart(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
}

function personOptionLabel(person) {
  return `${person.displayName || 'Assigned tester'} · ${person.sandboxLabel || person.sandboxId || 'Assigned sandbox'}`;
}

function sessionOptionLabel(session) {
  const who = session.displayName || 'Assigned tester';
  const when = formatSessionStart(session.startedAt);
  return when ? `${who} · ${when}` : `${who} · ${session.sessionId}`;
}

function environmentOptionLabel(entry) {
  if (entry.label && entry.label !== entry.environment) {
    return `${entry.label} (${entry.environment})`;
  }
  return entry.label || entry.environment;
}

function routeOptionLabel(route) {
  if (route.routeTitle) return `${route.routeTitle} (${route.routePath})`;
  return route.routePath;
}

function statusTone(status) {
  switch (`${status || ''}`.toLowerCase()) {
    case 'completed':
      return 'sage';
    case 'running':
      return 'blue';
    case 'queued':
      return 'amber';
    case 'error':
      return 'brick';
    default:
      return 'outline';
  }
}

function formatDateTime(value) {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not available' : parsed.toLocaleString();
}

function actorDisplay(event) {
  return event.actorDisplayName || 'Assigned tester';
}

function environmentDisplay(event) {
  return event.environmentLabel || event.environment || 'Unknown environment';
}

function activityDetail(event) {
  return event.targetLabel || event.fieldName || 'No additional detail';
}

function mergeNewRun(previous, nextRun) {
  const current = previous?.data || [];
  const nextData = [nextRun, ...current.filter((run) => run.id !== nextRun.id)];
  return {
    ...(previous || {}),
    data: nextData,
    page: previous?.page || { limit: 20, offset: 0, total: nextData.length },
  };
}

export function ActivityReportsPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const browserTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );
  const serialization = useMemo(() => {
    try {
      return {
        filters: serializeActivityReportFilters(filters, { timeZone: browserTimeZone }),
        error: null,
      };
    } catch (error) {
      return {
        filters: { ...filters, from: '', to: '' },
        error: error instanceof Error ? error.message : 'Choose a valid report time range.',
      };
    }
  }, [browserTimeZone, filters]);
  const serializedFilters = serialization.filters;
  const reports = useApi('/activity-reports', { query: { limit: 20 } });
  const facets = useApi('/activity-reports/facets');
  const activityPreviewQuery = useMemo(
    () => ({
      actorUserId: filters.personId || undefined,
      sessionId: filters.sessionId || undefined,
      environment: filters.environment || undefined,
      routePath: filters.routePath || undefined,
      eventType: filters.eventType || undefined,
      eventName: filters.eventName || undefined,
      from: serializedFilters.from || undefined,
      to: serializedFilters.to || undefined,
      limit: 25,
    }),
    [filters, serializedFilters],
  );
  const recentActivity = useApi('/activity-events', { query: activityPreviewQuery });
  const refreshReports = reports.refresh;
  const refreshRecentActivity = recentActivity.refresh;
  const createRun = useMutation();
  const runs = useMemo(() => reports.data?.data || [], [reports.data]);
  const previewEvents = useMemo(() => recentActivity.data?.data || [], [recentActivity.data]);
  const facetData = facets.data?.data || null;
  const personOptions = facetData?.people || [];
  const sessionOptions = facetData?.sessions || [];
  const environmentOptions = facetData?.environments || [];
  const routeOptions = facetData?.routes || [];
  const previewSummary = useMemo(() => {
    const sessionIds = new Set(previewEvents.map((event) => event.sessionId).filter(Boolean));
    const environments = new Set(
      previewEvents.map((event) => environmentDisplay(event)).filter(Boolean),
    );
    return {
      eventCount: previewEvents.length,
      sessionCount: sessionIds.size,
      environmentCount: environments.size,
    };
  }, [previewEvents]);
  const hasPendingRuns = useMemo(
    () => runs.some((run) => ['queued', 'running'].includes(`${run.status || ''}`.toLowerCase())),
    [runs],
  );
  const eventNameOptions = useMemo(
    () => eventNameOptionsFor(filters.eventType),
    [filters.eventType],
  );

  useEffect(() => {
    if (!hasPendingRuns) return undefined;
    const interval = setInterval(() => {
      refreshReports();
    }, 5000);
    return () => clearInterval(interval);
  }, [hasPendingRuns, refreshReports]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshRecentActivity();
    }, 5000);
    return () => clearInterval(interval);
  }, [refreshRecentActivity]);

  function updateField(key, value) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function handleEventTypeChange(value) {
    setFilters((current) => {
      // Keep a chosen event name only if it still belongs to the newly selected type.
      const allowed = value ? activity.ACTIVITY_EVENT_NAMES_BY_TYPE[value] || [] : null;
      const nextEventName =
        current.eventName && allowed && !allowed.includes(current.eventName)
          ? ''
          : current.eventName;
      return { ...current, eventType: value, eventName: nextEventName };
    });
  }

  function handleEventNameChange(value) {
    setFilters((current) => {
      // Selecting a specific event implies its type — keep the pair consistent.
      const derivedType = value
        ? activity.resolveExpectedActivityEventType(value) || current.eventType
        : current.eventType;
      return { ...current, eventName: value, eventType: derivedType };
    });
  }

  function resetFilters() {
    setFilters(EMPTY_FILTERS);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (serialization.error) return;
    const response = await createRun
      .run(() => api.post('/activity-reports', serializedFilters))
      .catch(() => null);
    const nextRun = response?.data;
    if (nextRun) {
      reports.setData((current) => mergeNewRun(current, nextRun));
      setFilters((current) => ({ ...current, title: '' }));
    }
  }

  if (reports.loading && !reports.data) {
    return (
      <>
        <PageHeader title="Activity Reports" subtitle="Loading recent investigator reports." />
        <LoadingSkeleton rows={6} />
      </>
    );
  }

  if (reports.error && !reports.data) {
    return <ErrorState error={reports.error} />;
  }

  return (
    <>
      <PageHeader
        eyebrow="Administration · Security review"
        title="Activity Reports"
        subtitle="Request a filtered investigator report, track generation status, and open the finished artifact inside DHA PRIME."
      />

      <section className="bento admin-bento">
        <div className="span-5 card">
          <div className="row items-center justify-between">
            <h2 className="card-title">Generate report</h2>
            <button type="button" className="btn ghost sm" onClick={resetFilters}>
              Clear filters
            </button>
          </div>
          <p className="muted small activity-form-lede">
            Choose an active sandbox tester to focus the evidence, or leave Person on all eligible
            testers for the complete sandbox roster.
          </p>
          {serialization.error ? (
            <p className="activity-report-time-error" role="alert">
              {serialization.error}
            </p>
          ) : null}

          <form className="activity-filter-form" onSubmit={handleSubmit}>
            <fieldset className="activity-filter-group">
              <legend className="activity-filter-legend">Who to investigate</legend>
              <div className="activity-filter-grid">
                <label className="form-field activity-filter-field--full">
                  <span className="form-label">Person</span>
                  <div className="form-select-wrap">
                    <select
                      className="form-input form-select"
                      aria-label="Person"
                      value={filters.personId}
                      onChange={(event) => updateField('personId', event.target.value)}
                    >
                      <option value="">All eligible testers</option>
                      {personOptions.map((person) => (
                        <option key={person.personId} value={person.personId}>
                          {personOptionLabel(person)}
                        </option>
                      ))}
                    </select>
                    <span className="form-select-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </div>
                </label>
                <label className="form-field activity-filter-field--full">
                  <span className="form-label">Session</span>
                  <div className="form-select-wrap">
                    <select
                      className="form-input form-select"
                      aria-label="Session"
                      value={filters.sessionId}
                      onChange={(event) => updateField('sessionId', event.target.value)}
                    >
                      <option value="">Any session</option>
                      {sessionOptions.map((session) => (
                        <option key={session.sessionId} value={session.sessionId}>
                          {sessionOptionLabel(session)}
                        </option>
                      ))}
                    </select>
                    <span className="form-select-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </div>
                </label>
                <label className="form-field activity-filter-field--full">
                  <span className="form-label">Environment</span>
                  <div className="form-select-wrap">
                    <select
                      className="form-input form-select"
                      aria-label="Environment"
                      value={filters.environment}
                      onChange={(event) => updateField('environment', event.target.value)}
                    >
                      <option value="">Any environment</option>
                      {environmentOptions.map((entry) => (
                        <option key={entry.environment} value={entry.environment}>
                          {environmentOptionLabel(entry)}
                        </option>
                      ))}
                    </select>
                    <span className="form-select-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </div>
                </label>
              </div>
            </fieldset>

            <fieldset className="activity-filter-group">
              <legend className="activity-filter-legend">What happened</legend>
              <div className="activity-filter-grid">
                <label className="form-field">
                  <span className="form-label">Event type</span>
                  <div className="form-select-wrap">
                    <select
                      className="form-input form-select"
                      aria-label="Event type"
                      value={filters.eventType}
                      onChange={(event) => handleEventTypeChange(event.target.value)}
                    >
                      <option value="">Any activity type</option>
                      {EVENT_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="form-select-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </div>
                </label>
                <label className="form-field">
                  <span className="form-label">Event name</span>
                  <div className="form-select-wrap">
                    <select
                      className="form-input form-select"
                      aria-label="Event name"
                      value={filters.eventName}
                      onChange={(event) => handleEventNameChange(event.target.value)}
                    >
                      <option value="">Any event</option>
                      {eventNameOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="form-select-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </div>
                </label>
                <label className="form-field activity-filter-field--full">
                  <span className="form-label">Route path</span>
                  <div className="form-select-wrap">
                    <select
                      className="form-input form-select"
                      aria-label="Route path"
                      value={filters.routePath}
                      onChange={(event) => updateField('routePath', event.target.value)}
                    >
                      <option value="">Any page</option>
                      {routeOptions.map((route) => (
                        <option key={route.routePath} value={route.routePath}>
                          {routeOptionLabel(route)}
                        </option>
                      ))}
                    </select>
                    <span className="form-select-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </div>
                </label>
              </div>
            </fieldset>

            <fieldset className="activity-filter-group">
              <legend className="activity-filter-legend">When it happened</legend>
              <div className="activity-filter-grid">
                <label className="form-field">
                  <span className="form-label">From</span>
                  <input
                    className="form-input"
                    type="datetime-local"
                    value={filters.from}
                    onChange={(event) => updateField('from', event.target.value)}
                  />
                </label>
                <label className="form-field">
                  <span className="form-label">To</span>
                  <input
                    className="form-input"
                    type="datetime-local"
                    value={filters.to}
                    onChange={(event) => updateField('to', event.target.value)}
                  />
                </label>
              </div>
              <p className="muted small" data-testid="activity-report-timezone">
                Times are interpreted in {browserTimeZone} and submitted as exact UTC instants.
              </p>
            </fieldset>

            <fieldset className="activity-filter-group">
              <legend className="activity-filter-legend">Report details</legend>
              <label className="form-field">
                <span className="form-label">Report title</span>
                <input
                  className="form-input"
                  placeholder="Optional — a name helps you find this run later"
                  value={filters.title}
                  onChange={(event) => updateField('title', event.target.value)}
                />
              </label>
            </fieldset>

            <div className="activity-filter-actions">
              <button type="submit" className="btn primary" disabled={createRun.running}>
                {createRun.running ? 'Generating…' : 'Generate report'}
              </button>
              {createRun.error ? (
                <div className="form-error" role="alert">
                  <strong>{createRun.error.message}</strong>
                  {activityReportScopeErrorMessages(createRun.error).length ? (
                    <ul>
                      {activityReportScopeErrorMessages(createRun.error).map((message) => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          </form>
        </div>

        <div className="span-7 card">
          <div className="row items-center justify-between">
            <h2 className="card-title">Recent captured activity</h2>
            <div className="row items-center gap-8 wrap">
              <span className="chip outline sm">{previewSummary.eventCount} visible events</span>
              <span className="chip outline sm">{previewSummary.sessionCount} sessions</span>
              <span className="chip outline sm">
                {previewSummary.environmentCount} environments
              </span>
            </div>
          </div>
          <p className="muted small activity-form-lede">
            This preview is server-scoped to active sandbox testers and refreshes automatically so
            admins can confirm captured activity before generating a formal report.
          </p>
          {recentActivity.error ? (
            <div className="form-error" role="alert">
              {recentActivity.error.message}
            </div>
          ) : null}
          {previewEvents.length ? (
            <div className="admin-list">
              {previewEvents.map((event) => (
                <article key={event.id} className="admin-list-row">
                  <div className="admin-list-row__body">
                    <div className="row items-center gap-8 wrap">
                      <strong className="admin-list-row__title">
                        {activity.activityEventNameLabel(event.eventName)}
                      </strong>
                      <span className="chip outline sm">
                        {activity.activityEventTypeLabel(event.eventType)}
                      </span>
                    </div>
                    <div className="muted small">
                      {actorDisplay(event)} · {environmentDisplay(event)} ·{' '}
                      {formatDateTime(event.clientOccurredAt)}
                    </div>
                    <div className="muted small">
                      {routeOptionLabel({
                        routePath: event.routePath || '(unknown route)',
                        routeTitle: event.routeTitle,
                      })}{' '}
                      · {activityDetail(event)}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No captured activity yet"
              description="Open a PRIME page as a tester and return here to confirm the captured events and environment attribution."
            />
          )}
        </div>

        <div className="span-12 card">
          <div className="row items-center justify-between">
            <h2 className="card-title">Recent report runs</h2>
            {hasPendingRuns ? (
              <span className="chip blue sm live">Polling active runs…</span>
            ) : null}
          </div>
          {runs.length ? (
            <div className="admin-list">
              {runs.map((run) => (
                <article key={run.id} className="admin-list-row">
                  <div className="admin-list-row__body">
                    <div className="row items-center gap-8 wrap">
                      <strong className="admin-list-row__title">{run.title || run.id}</strong>
                      <span className={`chip ${statusTone(run.status)} sm`}>
                        {activity.activityReportStatusLabel(run.status)}
                      </span>
                    </div>
                    <div className="muted small">
                      Requested by{' '}
                      {run.requestedByDisplayName || run.requestedByUserId || 'Unknown'} ·{' '}
                      {formatDateTime(run.createdAt)}
                    </div>
                  </div>
                  <div className="admin-list-row__actions">
                    <Link className="btn secondary sm" to={`/admin/activity/reports/${run.id}`}>
                      Open in PRIME
                    </Link>
                    {run.outputUrl ? (
                      <a
                        className="btn tonal sm"
                        href={run.outputUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open artifact
                      </a>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No activity reports yet"
              description="Generate the first investigator report to review captured DHA PRIME activity."
            />
          )}
        </div>
      </section>
    </>
  );
}
